import {formatQueryParams} from './lib/toQuery.ts';

// Settings (customer administration). Organised as group/page so the shell's
// sidebar, the breadcrumb and the URL all say the same thing. Old flat tab
// paths and the former top-nav admin pages live under `legacy` and redirect.
const settings = {
    root: () => '/settings',

    // Company
    general: () => '/settings/general',
    access: () => '/settings/access',
    billing: () => '/settings/billing',

    // Capacity rules (was the Working Hours page)
    capacity: {
        workingHours: () => '/settings/capacity/working-hours',
        ragThresholds: () => '/settings/capacity/rag-thresholds',
        utilisation: () => '/settings/capacity/utilisation',
        nonWorkingDays: () => '/settings/capacity/non-working-days'
    },

    // Directory (was Roles / Tags in the top nav)
    directory: {
        roles: () => '/settings/directory/roles',
        roleDetail: (id: string) => `/settings/directory/roles/${id}`,
        tags: () => '/settings/directory/tags',
        sync: () => '/settings/directory/sync',
        syncGroups: () => '/settings/directory/sync/groups',
        syncRun: (runId: string) => `/settings/directory/sync/runs/${runId}`
    },

    // Integrations
    integrations: {
        overview: () => '/settings/integrations',
        imports: () => '/settings/integrations/imports',
        // ?format= preselects a data format in the wizard (e.g. people, projects).
        importNew: (params?: {format?: string}) =>
            `/settings/integrations/imports/new${formatQueryParams(params)}`,
        importPackage: () => '/settings/integrations/imports/package',
        importRun: (runId: string) =>
            `/settings/integrations/imports/runs/${runId}`,
        sources: () => '/settings/integrations/sources',
        clients: () => '/settings/integrations/clients',
        clientDetail: (id: string) => `/settings/integrations/clients/${id}`
    },

    // AI & MCP
    ai: {
        assistants: () => '/settings/ai/assistants',
        usage: () => '/settings/ai/usage',
        providerKeys: () => '/settings/ai/provider-keys',
        mcpServers: () => '/settings/ai/mcp-servers',
        connections: () => '/settings/ai/connections',
        mcpAccess: () => '/settings/ai/mcp-access',
        mcpClients: () => '/settings/ai/mcp-clients',
        mcpGrants: () => '/settings/ai/mcp-connections',
        mcpInfo: () => '/settings/ai/mcp-info'
    },

    // Audit
    activity: (params?: Record<string, unknown>) =>
        `/settings/activity${formatQueryParams(params)}`,

    // Paths that existed before the settings redesign (2026-09). Registered as
    // redirects so bookmarks and the user docs keep working.
    legacy: {
        accessControl: () => '/settings/access-control',
        aiProviders: () => '/settings/ai-providers',
        aiMcp: () => '/settings/ai-mcp',
        aiConnections: () => '/settings/ai-connections',
        mcpAccess: () => '/settings/mcp-access',
        mcpInfo: () => '/settings/mcp-info',
        workingHours: () => '/working-hours',
        roles: () => '/roles',
        roleDetail: () => '/roles/:id',
        tags: () => '/tags'
    }
};

// PARITY: three other places describe these routes and must move with them.
// - Certavue/RoutePaths.cs (RoutePaths.App): the server-side mirror used for
//   links in tool results and emails.
// - Certavue/Assistants/Tools/NavigateAssistantTool.cs: the assistant's
//   navigate_to vocabulary (list pages, insight keys, settings keys). A new
//   user-facing page or insight is added there so the assistant can open it;
//   src/assistant/navigationToolParity.test.ts fails if the tool names a path that is
//   not here.
// - src/pages.ts: the route table; see the PARITY note above `pages`.
export const RoutePaths = {
    // Home
    home: () => '/',

    // Marketing
    features: () => '/features',
    productTour: () => '/product-tour',
    whyCertavue: () => '/why-certavue',
    pricing: () => '/pricing',
    aboutUs: () => '/about-us',
    privacy: () => '/privacy',
    termsAndConditions: () => '/terms-and-conditions',

    // MS Auth
    msAuth: () => '/ms_auth',

    // MCP OAuth consent
    mcpConsent: (params?: Record<string, unknown>) =>
        `/mcp-consent${formatQueryParams(params)}`,

    accounts: {
        login: () => '/login',
        signUp: (params?: Record<string, unknown>) =>
            `/sign-up${formatQueryParams(params)}`,
        passwordlessLogin: () => '/passwordless-login',
        profile: () => '/profile',
        changePassword: () => '/change-password',
        forgotPassword: () => '/forgot-password',
        resetPassword: () => '/reset-password',
        emailConfirmation: () => '/email-confirmation',
        userRegistrationSocial: () => '/user-registration-social'
    },

    // PARITY: RoutePaths.cs (App.GettingStarted) and navigate_to's 'getting-started' page.
    // ?welcome=1 shows the just-registered welcome header.
    gettingStarted: (params?: {welcome?: boolean}) =>
        `/getting-started${formatQueryParams(params?.welcome ? {welcome: 1} : undefined)}`,

    company: {
        register: (params?: Record<string, unknown>) =>
            `/register-company${formatQueryParams(params)}`,
        completed: () => '/register-company-completed'
    },

    // ?startWeek=N[&weeks=M][&view=project|resource|utilisation|delivery]
    // deep-links the grid (see DashboardPage hydration).
    dashboard: (params?: Record<string, unknown>) =>
        `/dashboard${formatQueryParams(params)}`,

    projects: {
        root: () => '/projects',
        detail: (id: string) => `/projects/${id}`
    },

    teams: {
        root: () => '/teams',
        detail: (id: string) => `/teams/${id}`
    },

    insights: {
        root: () => '/insights',
        explore: (params?: Record<string, unknown>) =>
            `/insights/explore${formatQueryParams(params)}`,
        // Every insight keeps its selection in the querystring so a slice is
        // shareable by URL, and every view accepts startWeek/weeks to pin the
        // period (the shell's Copy link writes them). The params each view
        // reads, so links between insights can be built with these:
        //   individualLoad: person
        //   teams: scope=mine|all, show=all|overloaded
        //   projects: scope=mine|all, show=all|at-risk
        //   missingResources: by=project|role
        //   openRoleCandidates: scope=org|mine|teams, teams, refine, types,
        //                       assignment
        //   roleAvailability: mode=role|tag, roles, tags, show=all
        //   rolePressure: (none)
        //   roleCapacity: show=short, role
        //   sustainedOverload: mode=teamTag|teams, areas
        //   rebalancing: type=both|additional|baseline, person
        //   crunchTimeline: week
        //   demandByInitiative: grain=project|tag, type
        //   board: units
        //   underUtilised: team
        //   changeImpact: work, programme, projects, audience, dimension,
        //                 teams, peopleTag, demand, involvement, layout
        //   receivingLoad: (none)
        //   programmeImpact: tags, areas, mode, type
        //   adoption: audience, dimensionTagId, teamIds, peopleTagId, work,
        //             programmeTagId, projectIds
        individualLoad: (params?: Record<string, unknown>) =>
            `/insights/individual-load${formatQueryParams(params)}`,
        teams: (params?: Record<string, unknown>) =>
            `/insights/teams${formatQueryParams(params)}`,
        projects: (params?: Record<string, unknown>) =>
            `/insights/projects${formatQueryParams(params)}`,
        missingResources: (params?: Record<string, unknown>) =>
            `/insights/missing-resources${formatQueryParams(params)}`,
        openRoleCandidates: (params?: Record<string, unknown>) =>
            `/insights/open-role-candidates${formatQueryParams(params)}`,
        roleAvailability: (params?: Record<string, unknown>) =>
            `/insights/role-availability${formatQueryParams(params)}`,
        rolePressure: (params?: Record<string, unknown>) =>
            `/insights/role-pressure${formatQueryParams(params)}`,
        roleCapacity: (params?: Record<string, unknown>) =>
            `/insights/role-capacity${formatQueryParams(params)}`,
        sustainedOverload: (params?: Record<string, unknown>) =>
            `/insights/sustained-overload${formatQueryParams(params)}`,
        rebalancing: (params?: Record<string, unknown>) =>
            `/insights/rebalancing${formatQueryParams(params)}`,
        crunchTimeline: (params?: Record<string, unknown>) =>
            `/insights/crunch-timeline${formatQueryParams(params)}`,
        demandByInitiative: (params?: Record<string, unknown>) =>
            `/insights/demand-by-initiative${formatQueryParams(params)}`,
        board: (params?: Record<string, unknown>) =>
            `/insights/board${formatQueryParams(params)}`,
        underUtilised: (params?: Record<string, unknown>) =>
            `/insights/under-utilised${formatQueryParams(params)}`,
        changeImpact: (params?: Record<string, unknown>) =>
            `/insights/change-impact${formatQueryParams(params)}`,
        receivingLoad: (params?: Record<string, unknown>) =>
            `/insights/receiving-load${formatQueryParams(params)}`,
        programmeImpact: (params?: Record<string, unknown>) =>
            `/insights/programme-impact${formatQueryParams(params)}`,
        adoption: (params?: Record<string, unknown>) =>
            `/insights/adoption${formatQueryParams(params)}`
    },

    people: {
        root: () => '/people',
        detail: (id: string) => `/people/${id}`
    },

    // Roles, tags and working hours now live inside Settings; these aliases keep
    // every existing link (person page, allocation table, registration flow)
    // pointing at the right place without a sweep.
    roles: {
        root: () => settings.directory.roles(),
        detail: (id: string) => settings.directory.roleDetail(id)
    },

    tags: () => settings.directory.tags(),

    workingHours: () => settings.capacity.workingHours(),

    notifications: {
        root: () => '/notifications',
        settings: () => '/notifications/settings',
        demoEmail: () => '/notifications/demo-email'
    },

    documents: {
        root: () => '/documents',
        base: () => '/documents/',
        view: (path: string) => `/documents/${path}`
    },

    sysdocs: {
        root: () => '/sysdocs',
        base: () => '/sysdocs/',
        view: (path: string) => `/sysdocs/${path}`
    },

    settings,

    // Platform administration (sysadmins). The console shell lives at /sysadmin.
    sysadmin: {
        root: () => '/sysadmin',
        ai: {
            settings: () => '/sysadmin/ai/settings',
            usage: () => '/sysadmin/ai/usage',
            providers: () => '/sysadmin/ai/providers',
            providerEdit: (id: string) => `/sysadmin/ai/providers/${id}`,
            providerNew: () => '/sysadmin/ai/providers/_',
            providerModelEdit: (providerId: string, modelId: string) =>
                `/sysadmin/ai/providers/${providerId}/models/${modelId}`,
            providerModelNew: (providerId: string) =>
                `/sysadmin/ai/providers/${providerId}/models/_`,
            assistants: () => '/sysadmin/ai/assistants',
            assistantEdit: (id: string) => `/sysadmin/ai/assistants/${id}`,
            assistantNew: () => '/sysadmin/ai/assistants/_',
            assistantTest: (keyOrId: string) =>
                `/sysadmin/ai/assistants/${keyOrId}/test`,
            skills: () => '/sysadmin/ai/skills'
        },
        theme: () => '/sysadmin/theme',
        debug: () => '/sysadmin/debug',
        users: {
            root: () => '/sysadmin/users',
            edit: (id: string) => `/sysadmin/users/${id}`,
            create: () => '/sysadmin/users/_'
        },
        documents: {
            root: () => '/sysadmin/documents',
            edit: (id: string, params?: Record<string, unknown>) =>
                `/sysadmin/documents/edit/${id}${formatQueryParams(params)}`,
            create: () => '/sysadmin/documents/edit/_'
        },
        contents: {
            root: () => '/sysadmin/contents',
            upload: (params?: Record<string, unknown>) =>
                `/sysadmin/contents/upload${formatQueryParams(params)}`,
            preview: (filePath: string) =>
                `/sysadmin/contents/preview/${filePath}`
        },
        data: () => '/sysadmin/data',
        customers: {
            root: () => '/sysadmin/customers',
            edit: (id: string) => `/sysadmin/customers/${id}`
        },
        billing: () => '/sysadmin/billing',
        secretKeys: () => '/sysadmin/secret-keys',
        paddleWebhooks: () => '/sysadmin/paddle-webhooks'
    },

    // Fallback route
    notFound: () => '*'
};

export type RoutePaths = typeof RoutePaths;
